import ks from "../ReUsableTS/kBrowserShared";
import { kBucketDragDrop } from "./kBucketDragDrop";
import { kCsvCore } from "../kReusableCoreTS/kCsvCore";
import { kEditableTableHeading } from "./kEditableTableHeading";
import { AsKTimeOrNull, kHTMLEditableColType } from "./kEditableTableTypes";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableRow } from "./kEditableTableRow";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableColumn } from "./kEditableColumn";
import type { kTime } from "../ReUsableTS/kBrowserObjects";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTableSortDrag {
    public static SetRowSaveState(Self: kEditableTable, Index: number, State: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Row: kEditableTableRow = Self.Rows.Items[Index];
            if (Row === undefined || Row.RowNumberCell === null) { return; }
            const Cell: HTMLTableCellElement = Row.RowNumberCell;
            Cell.classList.remove("is-saving", "is-saved", "is-error");
            if (State === "") { return; }
            Cell.classList.add("is-" + State);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.SetRowSaveState");
        }
    }

    // Activate drag-reorder on the row-number column. Called automatically
    // from ToContainer() when EnableRowReorder is true; can also be called
    // manually after rows are added programmatically.
    public static ActivateDragReorder(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            for (const Row of Self.Rows.Items) {
                Self["_WireRowDrag"](Row);
            }
            Self.Table.addEventListener("dragover", Self.OnRowDragOver);
            Self.Table.addEventListener("drop", Self.OnRowDrop);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.ActivateDragReorder");
        }
    }

    public static _WireRowDrag(Self: kEditableTable, Row: kEditableTableRow): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Cell: HTMLTableCellElement | null = Row.RowNumberCell;
            if (Cell === null) { return; }
            Cell.setAttribute("draggable", "true");
            Cell.classList.add("kEditableTableDragHandle");
            Cell.addEventListener("dragstart", Self.OnRowDragStart);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._WireRowDrag");
        }
    }

    public static OnRowDragStart(Self: kEditableTable, Evt: DragEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Cell: HTMLElement = Evt.target as HTMLElement;
            const RowEl: HTMLTableRowElement | null = Cell.parentElement as HTMLTableRowElement | null;
            if (RowEl === null) { return; }
            for (let i = 0; i < Self.Rows.Items.length; i = i + 1) {
                if (Self.Rows.Items[i].Row === RowEl) {
                    Self._DraggedRowIndex = i;
                    if (Evt.dataTransfer !== null) {
                        Evt.dataTransfer.effectAllowed = "move";
                        Evt.dataTransfer.setData("text/plain", i.toString());
                    }
                    return;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.OnRowDragStart");
        }
    }

    public static OnRowDragOver(Self: kEditableTable, Evt: DragEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self._DraggedRowIndex < 0) { return; }
            kBucketDragDrop.SetMoveDropEffect(Evt, "kEditableTable.OnRowDragOver");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.OnRowDragOver");
        }
    }

    public static OnRowDrop(Self: kEditableTable, Evt: DragEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Evt.preventDefault();
            if (Self._DraggedRowIndex < 0) { return; }
            const Target: HTMLElement = Evt.target as HTMLElement;
            const TargetRow: HTMLTableRowElement | null = Target.closest("tr") as HTMLTableRowElement | null;
            if (TargetRow === null) { return; }
            let TargetIdx: number = -1;
            for (let i = 0; i < Self.Rows.Items.length; i = i + 1) {
                if (Self.Rows.Items[i].Row === TargetRow) { TargetIdx = i; break; }
            }
            if (TargetIdx < 0 || TargetIdx === Self._DraggedRowIndex) {
                Self._DraggedRowIndex = -1;
                return;
            }
            const Old: number = Self._DraggedRowIndex;
            Self["_MoveRow"](Old, TargetIdx);
            Self._DraggedRowIndex = -1;
            if (Self.RowReorderedCB !== null) { Self.RowReorderedCB(Old, TargetIdx); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.OnRowDrop");
        }
    }

    public static _MoveRow(Self: kEditableTable, FromIdx: number, ToIdx: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Row: kEditableTableRow = Self.Rows.Items[FromIdx];
            Self.Rows.Items.splice(FromIdx, 1);
            Self.Rows.Items.splice(ToIdx, 0, Row);
            // Re-append each row in the new array order. <th> headers stay
            // in their original positions because we don't touch them.
            for (const R of Self.Rows.Items) {
                if (R.Row !== null) { Self.Table.appendChild(R.Row); }
            }
            Self.UpdateRowNumbers();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._MoveRow");
        }
    }

    // CSV/TSV import shares the quote-aware record parser, then the existing cell
    // paste handler. Clipboard paste still accepts raw tab lines. The caller
    // owns the file picker; the shared component stays headless on UI.
    public static ImportCsvText(Self: kEditableTable, Text: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Text === "") { return; }
            // Park the cursor at the first cell of the first empty-looking row.
            const Target: kEditableTableCell = Self.GetCellByIndexes(0, 0);
            if (Target.Cell === undefined) { return; }
            Self.CurrenEditableCell = Target;
            const Lines: string[][] = kCsvCore.Rows(Text, kCsvCore.DelimiterOf(Text));
            Self.PasteInProgress = true;
            Self["_PasteMultiCell"](Lines);
            Self.PasteInProgress = false;
            Self.UpdateRowCount(Self.Rows.Items.length);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.ImportCsvText");
        }
    }

    public static _CreateColumnHeaders(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Headers = [];
            let FrozenLeftOffset: number = 30;
            for(const CurrCol of Self.Columns.Items){
                FrozenLeftOffset = Self["_BuildColumnHeader"](CurrCol, FrozenLeftOffset);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._CreateColumnHeaders");
        }
    }

    // Build, style, append and register one column's <th>. Returns the running
    // frozen-left offset (advanced only when this column is frozen) so the caller
    // threads it to the next column. Extracted from _CreateColumnHeaders for the
    // 40-line limit; behaviour is unchanged.
    public static _BuildColumnHeader(Self: kEditableTable, CurrCol: kEditableColumn, FrozenLeftOffset: number): number {
        if (ks.KErrorMode === true) { return FrozenLeftOffset; }
        try {
            const NewHeader:kEditableTableHeading = new kEditableTableHeading(CurrCol.Title,true,CurrCol.Icon);
            if(NewHeader && NewHeader.HeaderCell){
                NewHeader.ParentTable = Self;
                NewHeader.ColumnIndex = Self.Headers.length;
                NewHeader.HeaderCell.setAttribute("role", "columnheader");
                // Mirror the locked flag, CssClass and TintColor on the header (as _CreateContentCell
                // does on its cells) so a read-only column's heading matches its cells' styling.
                CurrCol.ApplyStyleTo(NewHeader.HeaderCell, "kEditableTableHeadingLocked");
                if (CurrCol.HeaderTooltip !== "") { NewHeader.HeaderCell.title = CurrCol.HeaderTooltip; }
                if(CurrCol.Width > 0){
                    NewHeader.HeaderCell.style.width = CurrCol.Width.toString() + "px";
                    NewHeader.HeaderCell.style.minWidth = CurrCol.Width.toString() + "px";
                }else{
                    NewHeader.HeaderCell.style.display = "none";
                }
                if (CurrCol.Frozen && CurrCol.Width > 0) {
                    CurrCol._FrozenLeft = FrozenLeftOffset;
                    NewHeader.HeaderCell.style.position = "sticky";
                    NewHeader.HeaderCell.style.left = FrozenLeftOffset + "px";
                    NewHeader.HeaderCell.style.zIndex = "3";
                    FrozenLeftOffset += CurrCol.Width;
                }
                Self.Table.appendChild(NewHeader.HeaderCell);
                Self.Headers.push(NewHeader);
                if (CurrCol.Resizable) { Self["_AddResizeHandle"](NewHeader); }
            }
            return FrozenLeftOffset;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._BuildColumnHeader");
            return FrozenLeftOffset;
        }
    }

    public static SortByColumn(Self: kEditableTable, ColumnIndex:number): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(Self.Headers.length > ColumnIndex){
                const CurrHeader:kEditableTableHeading = Self.Headers[ColumnIndex];
                if(CurrHeader){
                    if(CurrHeader.Sorted === false){
                        Self.SortAscending(ColumnIndex);
                    }else{
                        if(CurrHeader.SortedAscending === true){
                            Self.SortDescending(ColumnIndex);
                        }else{
                            Self.SortAscending(ColumnIndex);
                        }
                    }
                }
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.SortByColumn");
            return ;
        }
    }

    public static SortAscending(Self: kEditableTable, ColumnIndex?:number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_SortTo"](ColumnIndex, true, "kEditableTable.SortAscending");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.SortAscending");
        }
    }

    public static SortDescending(Self: kEditableTable, ColumnIndex?:number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_SortTo"](ColumnIndex, false, "kEditableTable.SortDescending");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.SortDescending");
        }
    }

    /** Moved from kEditableTable._SortTo (size split); the class member delegates here. */
    public static _SortTo(Self: kEditableTable, ColumnIndex: number | undefined, Ascending: boolean, Label: string): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(ColumnIndex === undefined){
                if(Self.RightClickColumn){
                    ColumnIndex = Self.RightClickColumn.ColumnIndex;
                }else{
                    ColumnIndex = 0;
                }
            }
            if(Self.Headers.length > ColumnIndex){
                const CurrHeader:kEditableTableHeading = Self.Headers[ColumnIndex];
                if(CurrHeader && CurrHeader.SortButton){
                    CurrHeader.SortedAscending = Ascending;
                    CurrHeader.Sorted = true;
                    if (Ascending === true) {
                        CurrHeader.SortButton.innerHTML = "&#8595;";
                        CurrHeader.SortButton.style.fontWeight = "bold";
                    } else {
                        CurrHeader.SortButton.innerHTML = "&#8593;";
                    }
                    CurrHeader.SortButton.style.color = "#0078d4";
                    if (Ascending === true) { CurrHeader.SetAriaSort("ascending"); } else { CurrHeader.SetAriaSort("descending"); }
                    Self.SortTable(ColumnIndex,Ascending);
                    if (Self.OnSorted !== null) { Self.OnSorted(ColumnIndex, Ascending); }
                    Self._ReapplyIfFiltering();
                }
                Self["_SetOtherUnsorted"](ColumnIndex);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._SortTo");
            return ;
        }
    }

    public static _GetSortableValue(Self: kEditableTable, Cell: kEditableTableCell): any {
        if (ks.KErrorMode === true) { return null; }
        try {
            const Val: any = Cell.CurrentValue;
            const SortDate: kTime | null = AsKTimeOrNull(Val);
            if (SortDate !== null) {
                return SortDate.ToISODate();
            }
            if (Cell.ColDef && Cell.ColDef.Type === kHTMLEditableColType.RichHtml) {
                // Sort by visible text, not by markup ("<b>Zebra</b>" must not
                // sort before "Apple" because of the tag).
                return ks.PlainTextFromHTML(ks.Nz(Val)).toLowerCase();
            }
            return Val;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._GetSortableValue");
            return null;
        }
    }

    public static _RowComparator(Self: kEditableTable, A: kEditableTableRow, B: kEditableTableRow): number {
        if (ks.KErrorMode === true) { return 0; }
        try {
            const ValA: any = Self["_GetSortableValue"](A.Cells[Self["_SortColumnIndex"]]);
            const ValB: any = Self["_GetSortableValue"](B.Cells[Self["_SortColumnIndex"]]);
            if (ValA === ValB) { return 0; }
            if (ValA === null || ValA === undefined) {
                if (Self["_SortAscending"] === true) { return -1; }
                return 1;
            }
            if (ValB === null || ValB === undefined) {
                if (Self["_SortAscending"] === true) { return 1; }
                return -1;
            }
            if (Self["_SortAscending"] === true) {
                if (ValA > ValB) { return 1; }
                return -1;
            }
            if (ValA < ValB) { return 1; }
            return -1;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._RowComparator");
            return 0;
        }
    }

    public static SortTable(Self: kEditableTable, ColumnIndex: number, Ascending: boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_SortColumnIndex"] = ColumnIndex;
            Self["_SortAscending"] = Ascending;
            const DataRows: kEditableTableRow[] = [];
            const EmptyRows: kEditableTableRow[] = [];
            let CurrRow: kEditableTableRow;
            for (CurrRow of Self.Rows.Items) {
                if (CurrRow.IsEmpty()) {
                    EmptyRows.push(CurrRow);
                } else {
                    DataRows.push(CurrRow);
                }
            }
            DataRows.sort(Self["_RowComparator"]);
            Self.Rows.Items = [...DataRows, ...EmptyRows];
            const Frag: DocumentFragment = document.createDocumentFragment();
            for (CurrRow of Self.Rows.Items) {
                if (CurrRow.Row) {
                    Frag.appendChild(CurrRow.Row);
                }
            }
            Self.Table.appendChild(Frag);
            Self.UpdateRowNumbers();
            Self.ApplyAlternateRowShading();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.SortTable");
            return;
        }
    }

    public static _SetOtherUnsorted(Self: kEditableTable, ColumnIndex:number): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(Self.Headers.length > ColumnIndex){
                for(const CurrHeader of Self.Headers){
                    if(CurrHeader !== Self.Headers[ColumnIndex]){
                        CurrHeader.SetUnsorted();
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._SetOtherUnsorted");
            return ;
        }
    }

    public static _AddResizeHandle(Self: kEditableTable, Header: kEditableTableHeading): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Header.HeaderCell === null) { return; }
            Header.HeaderCell.style.position = "relative";
            const Handle: HTMLDivElement = document.createElement("div");
            Handle.style.cssText = "position:absolute;right:0;top:0;bottom:0;width:5px;cursor:col-resize;user-select:none;";
            (Handle as any)._header = Header;
            Handle.addEventListener("mousedown", Self["_OnResizeMouseDown"]);
            Header.HeaderCell.appendChild(Handle);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._AddResizeHandle");
        }
    }

    public static _OnResizeMouseDown(Self: kEditableTable, Event: MouseEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Handle: HTMLDivElement = Event.currentTarget as HTMLDivElement;
            Self["_ResizingHeader"] = (Handle as any)._header as kEditableTableHeading;
            Self["_ResizeStartX"] = Event.clientX;
            Self["_ResizeStartWidth"] = 0;
            if (Self["_ResizingHeader"].HeaderCell) { Self["_ResizeStartWidth"] = Self["_ResizingHeader"].HeaderCell.offsetWidth; }
            document.addEventListener("mousemove", Self["_OnResizeMouseMove"]);
            document.addEventListener("mouseup", Self["_OnResizeMouseUp"]);
            Event.preventDefault();
            Event.stopPropagation();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._OnResizeMouseDown");
        }
    }

    public static _OnResizeMouseMove(Self: kEditableTable, Event: MouseEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self["_ResizingHeader"] === null || Self["_ResizingHeader"].HeaderCell === null) { return; }
            const Delta: number = Event.clientX - Self["_ResizeStartX"];
            const NewWidth: number = Math.max(30, Self["_ResizeStartWidth"] + Delta);
            Self["_ResizingHeader"].HeaderCell.style.width = NewWidth + "px";
            Self["_ResizingHeader"].HeaderCell.style.minWidth = NewWidth + "px";
            if (Self["_ResizingHeader"].ColumnIndex < Self.Columns.Items.length) {
                Self.Columns.Items[Self["_ResizingHeader"].ColumnIndex].Width = NewWidth;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._OnResizeMouseMove");
        }
    }

    public static _OnResizeMouseUp(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ResizingHeader"] = null;
            document.removeEventListener("mousemove", Self["_OnResizeMouseMove"]);
            document.removeEventListener("mouseup", Self["_OnResizeMouseUp"]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._OnResizeMouseUp");
        }
    }

    public static WrapInScrollContainer(Self: kEditableTable): HTMLDivElement {
        if (ks.KErrorMode === true) { return document.createElement("div"); }
        try {
            Self["_ScrollWrapper"] = document.createElement("div");
            Self["_ScrollWrapper"].className = "kEditableTableWrapper";
            Self["_ScrollWrapper"].style.overflowX = "auto";
            if (Self.VirtualScrollMaxHeight > 0) {
                Self["_ScrollWrapper"].style.maxHeight = Self.VirtualScrollMaxHeight + "px";
                Self["_ScrollWrapper"].style.overflowY = "auto";
            }
            Self["_ScrollWrapper"].appendChild(Self.Table);
            return Self["_ScrollWrapper"];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.WrapInScrollContainer");
            return document.createElement("div");
        }
    }

    public static SetVirtualScrollHeight(Self: kEditableTable, HeightPx: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.VirtualScrollMaxHeight = HeightPx;
            if (Self["_ScrollWrapper"]) {
                Self["_ScrollWrapper"].style.maxHeight = HeightPx + "px";
                Self["_ScrollWrapper"].style.overflowY = "auto";
            }
            Self["_SetupIntersectionObserver"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag.SetVirtualScrollHeight");
        }
    }

    public static _SetupIntersectionObserver(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.VirtualScrollMaxHeight === 0 || Self["_ScrollWrapper"] === null) { return; }
            if (Self["_RowObserver"]) { Self["_RowObserver"].disconnect(); }
            Self["_RowObserver"] = new IntersectionObserver(Self["_OnRowVisibilityChange"], {
                root: Self["_ScrollWrapper"],
                rootMargin: "200px 0px",
                threshold: 0
            });
            let CurrRow: kEditableTableRow;
            for (CurrRow of Self.Rows.Items) {
                if (CurrRow.Row) {
                    Self["_RowObserver"].observe(CurrRow.Row);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._SetupIntersectionObserver");
        }
    }

    public static _OnRowVisibilityChange(Self: kEditableTable, Entries: IntersectionObserverEntry[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let Entry: IntersectionObserverEntry;
            for (Entry of Entries) {
                const Row: HTMLTableRowElement = Entry.target as HTMLTableRowElement;
                if (Entry.isIntersecting) { Row.style.visibility = "visible"; }
                else { Row.style.visibility = "hidden"; }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSortDrag._OnRowVisibilityChange");
        }
    }
}
